import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Instagram, Mail } from "lucide-react";
import { toast } from "sonner";
import { NAV_PAGES, LINKS } from "@/data";

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [clicks, setClicks] = useState(0);
  const { pathname } = useLocation();
  const current = NAV_PAGES.find((p) => p.to === pathname) ||
    NAV_PAGES.find((p) => pathname.startsWith(p.to) && p.to !== "/") || NAV_PAGES[0];

  const logoClick = () => {
    const n = clicks + 1;
    setClicks(n);
    if (n === 5) {
      toast("still becoming.", { description: "— you found a hidden note. keep exploring." });
      setClicks(0);
    }
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 mix-blend-difference" data-testid="main-nav">
        <div className="flex items-center justify-between px-6 py-5 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={logoClick}
            className="font-display text-sm font-black uppercase tracking-[0.3em] text-white"
            aria-label="Kin Hakan — home"
          >
            <Link to="/">KIN©</Link>
          </button>
          <div className="flex items-center gap-6">
            <span className="hidden font-display text-[10px] uppercase tracking-[0.3em] text-white/70 md:block" data-testid="nav-page-indicator">
              {current.no} / {current.label}
            </span>
            <button
              data-testid="nav-open-button"
              onClick={() => setOpen(true)}
              data-cursor="OPEN"
              className="group flex items-center gap-3 font-display text-xs font-bold uppercase tracking-[0.3em] text-white"
            >
              Menu
              <span className="flex flex-col gap-1.5">
                <span className="h-px w-8 bg-white transition-[width] duration-300 group-hover:w-5" />
                <span className="h-px w-5 bg-white transition-[width] duration-300 group-hover:w-8" />
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-overlay"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[90] flex flex-col bg-ink text-paper"
          >
            <div className="flex items-center justify-between px-6 py-5 md:px-12">
              <span className="font-display text-sm font-black uppercase tracking-[0.3em]">KIN©</span>
              <button
                data-testid="nav-close-button"
                onClick={() => setOpen(false)}
                className="font-display text-xs font-bold uppercase tracking-[0.3em] text-paper/70 hover:text-ember"
              >
                Close ✕
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center px-6 md:px-12">
              {NAV_PAGES.map((p, i) => (
                <motion.div
                  key={p.to}
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={p.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-link-${p.label.toLowerCase()}`}
                    className={`group flex items-baseline gap-4 border-b border-white/10 py-2 md:py-3 ${
                      pathname === p.to ? "text-ember" : ""
                    }`}
                  >
                    <span className="font-display text-xs text-paper/40">{p.no}</span>
                    <span className="font-display text-4xl font-black uppercase tracking-tight transition-transform duration-300 group-hover:translate-x-4 md:text-6xl">
                      {p.label}
                    </span>
                    <ArrowUpRight className="ml-auto h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="flex items-center justify-between px-6 py-6 font-display text-[10px] uppercase tracking-[0.3em] text-paper/50 md:px-12">
              <a href={LINKS.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-ember" data-testid="nav-instagram-link">
                <Instagram className="h-3.5 w-3.5" /> @kinhakann
              </a>
              <span className="hidden md:block">Tangerang Selatan — Indonesia</span>
              <a href={`mailto:${LINKS.email}`} className="flex items-center gap-2 hover:text-ember" data-testid="nav-email-link">
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
